import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useUserRights } from '../../../context/UserRightsContext';
import { ApHubProvider } from './ApHubContext';
import { ThemeProvider } from '@mui/material/styles';
import harmonateTheme from '../../../theme/harmonateTheme';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import ApTreasuryDashboard from '../ApTreasuryDashboard';
import TreasuryPayment from '../TreasuryPayment';
import Invoices from '../Invoices';
import PaymentProposal from '../PaymentProposal';
import PaymentBatches from '../PaymentProposal/PaymentBatches';
import GlAssignments from '../GlAssignments/GlAssignments';
import BudgetVsPaymentIntelligence from '../BudgetVsPaymentIntelligence/BudgetVsPaymentIntelligence';
import { AP_HUB_BU_STORAGE_KEY, getDashboardDateRange } from './apHubStorage';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const mapPropertiesToBusinessUnits = (properties) =>
  (Array.isArray(properties) ? properties : [])
    .filter((p) => p.businessUnitId)
    .map((p) => ({
      businessUnitId: p.businessUnitId,
      businessUnitName: p.propertyName,
      businessUnitCode: p.propertyCode,
      propertyId: p.propertyId,
      parentBusinessUnitId: null,
      status: p.status || 'ACTIVE',
    }));

const fetchPropertyBusinessUnits = async (tenantId, { scope = 'user' } = {}) => {
  if (!tenantId) return [];

  const properties = await apiFetch(`conductor/cash-recon/properties/by-tenant?tenant_id=${tenantId}`);
  const list = Array.isArray(properties) ? properties : [];

  if (scope === 'tenant') {
    return mapPropertiesToBusinessUnits(list);
  }

  const userId = getUserId();
  if (!userId) {
    return mapPropertiesToBusinessUnits(list);
  }

  const userProps = await apiFetch(
    `conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${userId}`
  );
  const allowedPropertyIds = new Set(
    (Array.isArray(userProps) ? userProps : []).map((p) => p.propertyId)
  );

  return mapPropertiesToBusinessUnits(
    list.filter((p) => p.propertyId && allowedPropertyIds.has(p.propertyId))
  );
};

const AP_VIEWS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/treasury-payment',
    functionCode: 'Payment Intelligence',
  },
  // {
  //   id: 'treasury-payment',
  //   label: 'Treasury Payment',
  //   path: '/treasury-payment/workflows',
  //   functionCode: 'Payment Intelligence',
  // },
  {
    id: 'invoices',
    label: 'Invoices',
    path: '/treasury-payment/invoices',
    functionCode: 'Payment Intelligence',
  },
  {
    id: 'payment-proposal',
    label: 'Payment Proposal',
    path: '/treasury-payment/payment-proposal',
    functionCode: 'Payment Intelligence',
  },
];

const viewFromPath = (pathname) => {
  if (pathname.includes('/treasury-payment/invoices') || pathname === '/invoices') {
    return 'invoices';
  }
  if (pathname.includes('/treasury-payment/payment-batches')) {
    return 'payment-batches';
  }
  if (pathname.includes('/treasury-payment/gl-assignments')) {
    return 'gl-assignments';
  }
  if (pathname.includes('/treasury-payment/budget-vs-pi')) {
    return 'budget-vs-pi';
  }
  if (pathname.includes('/treasury-payment/payment-proposal') || pathname === '/payment-proposal') {
    return 'payment-proposal';
  }
  if (pathname.includes('/treasury-payment/workflows')) {
    return 'treasury-payment';
  }
  return 'dashboard';
};

const ApHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = getTenantId();
  const { hasPermission, canAccessMenu } = useUserRights();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);
  const [selectedBusinessUnitId, setSelectedBusinessUnitIdState] = useState(() => {
    if (viewFromPath(window.location.pathname) === 'dashboard') return '';
    return sessionStorage.getItem(AP_HUB_BU_STORAGE_KEY) || '';
  });

  const setSelectedBusinessUnitId = useCallback((businessUnitId) => {
    setSelectedBusinessUnitIdState(businessUnitId);
    if (businessUnitId) {
      sessionStorage.setItem(AP_HUB_BU_STORAGE_KEY, businessUnitId);
    } else {
      sessionStorage.removeItem(AP_HUB_BU_STORAGE_KEY);
    }
  }, []);

  const selectedBusinessUnitName = useMemo(() => {
    const match = businessUnits.find((bu) => bu.businessUnitId === selectedBusinessUnitId);
    if (!match) return '';
    const code = match.businessUnitCode || '';
    const name = match.businessUnitName || '';
    if (code && name) return `${code} - ${name}`;
    return name || code || '';
  }, [businessUnits, selectedBusinessUnitId]);

  useEffect(() => {
    if (!tenantId) {
      setBusinessUnits([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setBusinessUnitsLoading(true);
        const units = await fetchPropertyBusinessUnits(tenantId);
        if (!cancelled) setBusinessUnits(units);
      } catch (err) {
        console.error('Failed to load AP business units', err);
        if (!cancelled) setBusinessUnits([]);
      } finally {
        if (!cancelled) setBusinessUnitsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  useEffect(() => {
    if (!selectedBusinessUnitId || !businessUnits.length) return;
    const valid = businessUnits.some((bu) => bu.businessUnitId === selectedBusinessUnitId);
    if (!valid) setSelectedBusinessUnitId('');
  }, [businessUnits, selectedBusinessUnitId, setSelectedBusinessUnitId]);

  const availableViews = useMemo(
    () => AP_VIEWS.filter((view) => {
      if (canAccessMenu(view.functionCode)) return true;
      return hasPermission(view.functionCode, 'view');
    }),
    [canAccessMenu, hasPermission]
  );

  const activeViewId = viewFromPath(location.pathname);
  const isPaymentBatchesView = activeViewId === 'payment-batches';
  const isGlAssignmentsView = activeViewId === 'gl-assignments';
  const isBudgetVsPiView = activeViewId === 'budget-vs-pi';
  const activeView = isPaymentBatchesView
    ? { id: 'payment-batches' }
    : isGlAssignmentsView
      ? { id: 'gl-assignments' }
      : isBudgetVsPiView
        ? { id: 'budget-vs-pi' }
      : availableViews.find((v) => v.id === activeViewId) || availableViews[0];

  useEffect(() => {
    if (!availableViews.length) return;
    if (isPaymentBatchesView || isGlAssignmentsView || isBudgetVsPiView) return;
    if (!activeView || activeView.id !== activeViewId) {
      navigate(availableViews[0].path, { replace: true });
    }
  }, [activeView, activeViewId, availableViews, isPaymentBatchesView, isGlAssignmentsView, isBudgetVsPiView, navigate]);

  useEffect(() => {
    if (!selectedBusinessUnitId) {
      setMenuAnchor(null);
    }
  }, [selectedBusinessUnitId]);

  const handleNavigate = useCallback((view) => {
    setMenuAnchor(null);
    navigate(view.path);
  }, [navigate]);

  const handleNavigateGlAssignments = useCallback(() => {
    setMenuAnchor(null);
    navigate('/treasury-payment/gl-assignments');
  }, [navigate]);

  const handleNavigateBudgetVsPi = useCallback(() => {
    setMenuAnchor(null);
    navigate('/treasury-payment/budget-vs-pi');
  }, [navigate]);

  const canViewGlAssignments = useMemo(
    () => canAccessMenu('Payment Intelligence') || hasPermission('Payment Intelligence', 'view'),
    [canAccessMenu, hasPermission]
  );

  const canViewBudgetVsPi = canViewGlAssignments;

  const glCodesMenuState = useMemo(() => {
    const dateRange = getDashboardDateRange();
    const hasBusinessUnit = Boolean(selectedBusinessUnitId);
    const hasDateRange = Boolean(dateRange.from && dateRange.to);
    const canNavigate = hasBusinessUnit && hasDateRange;

    let tooltip = '';
    if (!hasBusinessUnit && !hasDateRange) {
      tooltip = 'Select a business unit and date range to view GL codes';
    } else if (!hasBusinessUnit) {
      tooltip = 'Select a business unit to view GL codes';
    } else if (!hasDateRange) {
      tooltip = 'Select a date range to view GL codes';
    }

    return { canNavigate, tooltip };
  }, [selectedBusinessUnitId, menuAnchor]);

  const renderModuleMenu = useCallback(() => {
    if (activeViewId !== 'dashboard' || availableViews.length <= 1) return null;
    const hasBusinessUnit = Boolean(selectedBusinessUnitId);
    return (
      <Tooltip
        title={hasBusinessUnit ? 'Switch AP module' : 'Select a business unit to navigate'}
      >
        <span>
          <IconButton
            size="small"
            disabled={!hasBusinessUnit}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="AP module menu"
            sx={{ color: ht.white, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)' } }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  }, [activeViewId, availableViews.length, selectedBusinessUnitId]);

  const canSelectBusinessUnit = activeViewId === 'dashboard';

  const hubContextValue = useMemo(() => ({
    renderModuleMenu,
    businessUnits,
    businessUnitsLoading,
    selectedBusinessUnitId,
    selectedBusinessUnitName,
    setSelectedBusinessUnitId,
    canSelectBusinessUnit,
    activeViewId,
  }), [
    renderModuleMenu,
    businessUnits,
    businessUnitsLoading,
    selectedBusinessUnitId,
    selectedBusinessUnitName,
    setSelectedBusinessUnitId,
    canSelectBusinessUnit,
    activeViewId,
  ]);

  const renderContent = () => {
    if (isPaymentBatchesView) return <PaymentBatches />;
    if (isGlAssignmentsView) return <GlAssignments />;
    if (isBudgetVsPiView) return <BudgetVsPaymentIntelligence />;
    if (!activeView) {
      return null;
    }
    if (activeView.id === 'invoices') return <Invoices />;
    if (activeView.id === 'payment-proposal') return <PaymentProposal />;
    if (activeView.id === 'treasury-payment') return <TreasuryPayment />;
    return <ApTreasuryDashboard />;
  };

  if (!availableViews.length) {
    return (
      <ThemeProvider theme={harmonateTheme}>
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            background: ht.pageBg,
            color: ht.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={harmonateTheme}>
    <ApHubProvider value={hubContextValue}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          background: ht.pageBg,
          color: ht.white,
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{renderContent()}</Box>
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {availableViews
          .filter((view) => view.id !== activeViewId)
          .map((view) => (
          <MenuItem
            key={view.id}
            onClick={() => handleNavigate(view)}
          >
            <ListItemText primary={view.label} />
          </MenuItem>
        ))}
        {activeViewId === 'dashboard' && canViewGlAssignments && (
          glCodesMenuState.tooltip ? (
            <Tooltip title={glCodesMenuState.tooltip} placement="left">
              <span>
                <MenuItem disabled>
                  <ListItemText primary="GL Codes" />
                </MenuItem>
              </span>
            </Tooltip>
          ) : (
            <MenuItem onClick={handleNavigateGlAssignments}>
              <ListItemText primary="GL Codes" />
            </MenuItem>
          )
        )}
        {activeViewId === 'dashboard' && canViewBudgetVsPi && (
          <MenuItem
            disabled={!selectedBusinessUnitId}
            onClick={handleNavigateBudgetVsPi}
          >
            <ListItemText primary="Budget vs Payment Intelligence" />
          </MenuItem>
        )}
      </Menu>
    </ApHubProvider>
    </ThemeProvider>
  );
};

export default ApHub;
